import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, MailQuestion, Sparkles, IndianRupee, Clock, AlertTriangle, CalendarDays, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/adminApi";
import { Badge, StatCard, EmptyState, fmtMoney, MONTH_LABEL } from "./shared";

export default function AdminDashboard() {
    const [data, setData] = useState(null);

    const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Could not load dashboard"));
    useEffect(() => { load(); }, []);

    if (!data) return <p className="text-sm text-muted-foreground">Loading dashboard…</p>;

    const revenue = Object.entries(data.monthly_revenue || {});
    const remind = async (pid) => {
        try {
            const r = await api.post(`/payments/${pid}/reminder`);
            if (!r.data.phone) return toast.error("No phone number on file for this student");
            window.open(r.data.url, "_blank");
        } catch {
            toast.error("Could not create reminder");
        }
    };
    const maxRev = Math.max(1, ...data.revenue_by_month.map((m) => m.revenue));

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-serif text-3xl font-semibold text-ink" data-testid="dashboard-title">Dashboard</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{data.current_month_label} at a glance</p>
                </div>
                <Link to="/admin/payments" className="rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream transition-colors hover:bg-teal" data-testid="dashboard-go-payments">
                    Manage Payments
                </Link>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-stats">
                <StatCard icon={Users} label="Active Students" value={data.active_students} testid="stat-active-students" />
                <StatCard icon={MailQuestion} label="New Enquiries" value={data.new_enquiries} tone="saffron" testid="stat-new-enquiries" />
                <StatCard icon={Sparkles} label="Trial Requests" value={data.trial_classes} tone="saffron" testid="stat-trials" />
                <StatCard icon={IndianRupee} label="Revenue This Month"
                    value={revenue.length ? revenue.map(([c, a]) => fmtMoney(a, c)).join(" + ") : fmtMoney(0)}
                    sub={`${data.paid_count} payment${data.paid_count === 1 ? "" : "s"} received`} testid="stat-revenue" />
                <StatCard icon={Clock} label="Pending Fees" value={data.pending_count} sub={data.current_month_label} testid="stat-pending" />
                <StatCard icon={AlertTriangle} label="Overdue Fees" value={data.overdue_count} tone="saffron" sub="From earlier months" testid="stat-overdue" />
                <StatCard icon={CalendarDays} label="Upcoming Classes" value={data.upcoming_classes.length} testid="stat-upcoming" />
                <StatCard icon={MessageCircle} label="Quick Answer" value={data.attention_payments.length ? `${data.attention_payments.length} need follow-up` : "All fees on track"} testid="stat-attention" />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="dashboard-revenue-chart">
                    <h2 className="font-serif text-xl font-semibold text-ink">Monthly Revenue</h2>
                    <div className="mt-6 flex h-44 items-end gap-3">
                        {data.revenue_by_month.map((m) => (
                            <div key={m.month} className="flex flex-1 flex-col items-center gap-2">
                                <span className="text-xs font-medium text-ink">{m.revenue ? fmtMoney(m.revenue) : "—"}</span>
                                <div className="w-full rounded-t-lg bg-teal/90 transition-all" style={{ height: `${Math.max(4, (m.revenue / maxRev) * 120)}px` }} />
                                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{m.label.slice(0, 3)}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="dashboard-upcoming">
                    <h2 className="font-serif text-xl font-semibold text-ink">Upcoming Classes</h2>
                    {data.upcoming_classes.length === 0 ? (
                        <p className="mt-6 text-sm text-muted-foreground">No classes scheduled yet. Add some from the Classes section.</p>
                    ) : (
                        <ul className="mt-4 space-y-3">
                            {data.upcoming_classes.slice(0, 6).map((c) => (
                                <li key={c.id} className="flex items-center justify-between rounded-xl bg-sand px-4 py-3 text-sm">
                                    <span className="font-medium text-ink">{c.student_name} · {c.subject}</span>
                                    <span className="text-muted-foreground">{c.date} · {c.time}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="dashboard-attention">
                <div className="flex items-center justify-between">
                    <h2 className="font-serif text-xl font-semibold text-ink">Fees Needing Attention</h2>
                    <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{MONTH_LABEL(data.current_month)} & earlier</span>
                </div>
                {data.attention_payments.length === 0 ? (
                    <p className="mt-6 text-sm text-muted-foreground">Nothing pending — every fee is paid. </p>
                ) : (
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                    <th className="pb-3 pr-4 font-medium">Student</th>
                                    <th className="pb-3 pr-4 font-medium">Month</th>
                                    <th className="pb-3 pr-4 font-medium">Amount</th>
                                    <th className="pb-3 pr-4 font-medium">Status</th>
                                    <th className="pb-3 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.attention_payments.map((p) => (
                                    <tr key={p.id} className="border-b border-border/60 last:border-0" data-testid={`attention-row-${p.id}`}>
                                        <td className="py-3 pr-4 font-medium text-ink">
                                            <Link to={`/admin/students/${p.student_id}`} className="hover:text-saffron">{p.student_name}</Link>
                                        </td>
                                        <td className="py-3 pr-4">{p.month_label}</td>
                                        <td className="py-3 pr-4">{fmtMoney(p.amount, p.currency)}</td>
                                        <td className="py-3 pr-4"><Badge value={p.display_status} /></td>
                                        <td className="py-3">
                                            <button onClick={() => remind(p.id)} data-testid={`remind-button-${p.id}`}
                                                className="rounded-full border border-teal/30 px-4 py-1.5 text-xs font-medium text-teal transition-colors hover:bg-teal hover:text-cream">
                                                Remind on WhatsApp
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
